import React, { useState, FormEvent, ChangeEvent } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { api } from "../../lib/axios";

interface LocationState {
  tempToken?: string;
}

interface Verify2FAResponse {
  token: string;
  message: string;
}

const Verify2FA: React.FC = () => {
  const [twoFactorCode, setTwoFactorCode] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { tempToken } = (location.state as LocationState) || {};

  if (!tempToken) {
    return <Navigate to="/login" replace />;
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setTwoFactorCode(value);
    setError("");
  };

  const handleVerify2FA = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await api.post<Verify2FAResponse>(
        "/verify-2fa",
        { token: twoFactorCode },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );

      setMessage("Verification successful!");
      localStorage.setItem("token", response.data.token);

      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 1500);
    } catch (error: any) {
      setError(error.response?.data?.message || "Verification failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 w-full flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Two-Factor Authentication
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Please enter the verification code from your authenticator app
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleVerify2FA}>
            <div>
              <label
                htmlFor="twoFactorCode"
                className="block text-sm font-medium text-gray-700"
              >
                Authentication Code
              </label>
              <div className="mt-1">
                <input
                  id="twoFactorCode"
                  name="twoFactorCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm tracking-widest text-center font-mono text-lg"
                  placeholder="000000"
                  value={twoFactorCode}
                  onChange={handleInputChange}
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>

            {(error || message) && (
              <div
                className={`p-4 rounded-md ${
                  error
                    ? "bg-red-50 text-red-700"
                    : "bg-green-50 text-green-700"
                }`}
              >
                <p className="text-sm font-medium">{error || message}</p>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading || twoFactorCode.length !== 6}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Verify Code
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Verify2FA;
